/**
 * O modelo de DOMÍNIO do front para a fila de triagem. Não é o contrato da
 * API — mesma distinção que `features/casos/api/tipos.ts` já documenta.
 *
 * O fio é `@anery/api-contracts`, em inglês, gerado do OpenAPI que a própria
 * API emite (`T9`). A tradução para pt-BR mora em `mapeadores.ts`, e só lá.
 *
 * Vocabulário mantido em inglês DE PROPÓSITO, pelo mesmo motivo de
 * `EstadoDoCaso`: `SituacaoDoPrazo` (`deadline.status`) e `OrigemDoCampo`
 * (`origin`) são tokens que trafegam — traduzir cedo faria o front comparar
 * `'Perto de estourar'` com o que a API mandou e errar em silêncio.
 */

import type {
  AuthorizationResult,
  AuthorizationResultSource,
  ClassificationReviewReason,
  ClassificationSource,
  ExtractionSource,
  MessageKind,
  Modality,
  RequestTypeClass,
  TriagemClinicalFieldSnapshot,
  TriagemDeadlineSource,
  TriagemDeadlineStatus,
  TriagemDetalhe,
  TriagemFilaItemResponse,
  TriagemFilaResponse,
} from '@anery/api-contracts';

import type { Instante } from '../dominio/datas';

/** Só para quem lê: os tipos do fio que o mapeador consome. */
export type { TriagemDetalhe, TriagemFilaItemResponse, TriagemFilaResponse };

/**
 * De onde veio o valor de um campo lido (`AC-8`): regra, agente, ou humano
 * (`valorHumano` preenchido depois de confirmação — `FC-4`, fora desta US).
 */
export type OrigemDoCampo = ExtractionSource;

export const ROTULOS_DE_ORIGEM: Record<OrigemDoCampo, string> = {
  Rule: 'Regra',
  Agent: 'Agente',
  Human: 'Humano',
};

export function ehOrigemDoCampo(valor: unknown): valor is OrigemDoCampo {
  return typeof valor === 'string' && Object.hasOwn(ROTULOS_DE_ORIGEM, valor);
}

/** De onde veio o prazo (`AC-13`/`E5`): extraído do e-mail, do ticket, ou nenhum. */
export type OrigemDoPrazo = TriagemDeadlineSource;

export const ROTULOS_DE_ORIGEM_DO_PRAZO: Record<OrigemDoPrazo, string> = {
  None: 'Sem prazo identificado',
  Extracted: 'Extraído do e-mail',
  Ticket: 'Prazo do ticket',
};

export function ehOrigemDoPrazo(valor: unknown): valor is OrigemDoPrazo {
  return typeof valor === 'string' && Object.hasOwn(ROTULOS_DE_ORIGEM_DO_PRAZO, valor);
}

/**
 * Situação do prazo (`AC-14`/`AC-15`), calculada NO SERVIDOR
 * (`TriagemDeadlineStatusCalculator`, `T7`) a partir do limiar configurado —
 * nunca recalculada aqui. É por isso que este módulo não importa
 * `shared/ui/SeloDeSla`/`shared/dominio/sla.ts`: aquele limiar é do front.
 */
export type SituacaoDoPrazo = TriagemDeadlineStatus;

export const ROTULOS_DE_SITUACAO_DO_PRAZO: Record<SituacaoDoPrazo, string> = {
  None: 'Sem prazo',
  OnTrack: 'No prazo',
  Approaching: 'Perto de estourar',
  Overdue: 'Vencido',
  // Só o Caso chega a estes dois: o ticket de triagem não tem instante de resposta.
  AnsweredOnTime: 'Respondido no prazo',
  AnsweredLate: 'Respondido fora do prazo',
};

export function ehSituacaoDoPrazo(valor: unknown): valor is SituacaoDoPrazo {
  return typeof valor === 'string' && Object.hasOwn(ROTULOS_DE_SITUACAO_DO_PRAZO, valor);
}

/**
 * Um campo lido do e-mail de origem (`AC-8`/`AC-9`/`AC-29`).
 *
 * `tentado: false` (nunca tentado) é visualmente distinto de
 * `tentado: true, encontrado: false` (tentou e não achou) — a tela decide o
 * texto, este tipo só carrega a distinção.
 */
export interface CampoDaTriagem {
  readonly tentado: boolean;
  readonly encontrado: boolean;
  readonly valor: string | null;
  readonly valorHumano: string | null;
  readonly confianca: number | null;
  readonly limiar: number | null;
  readonly origem: OrigemDoCampo | null;
  /**
   * `campo_extraido.id` (`TP-030`) — a chave que `PATCH
   * /triagem/{ticketId}/campos/{campoId}` usa para confirmar ESTE campo.
   * `null` no item de lista (`TicketDaTriagem`, esta entrega não expõe id
   * ali — `FC-4`) e sempre para `CampoClinico` (`cid`/`hd` não têm ação de
   * confirmação por valor, `D2`). Presente só no DETALHE
   * (`TriagemDetalhada`).
   */
  readonly id: string | null;
}

/**
 * `cid`/`hd` (`AC-31`, `TP-027`). SEM propriedade de valor — mesma
 * disciplina do tipo C# (`TriagemClinicalFieldSnapshot`, T3b/T5): impossível
 * representar, não só proibido. Um componente não tem como renderizar por
 * acidente o que o tipo não tem.
 */
export interface CampoClinico {
  readonly tentado: boolean;
  readonly encontrado: boolean;
}

/**
 * A classe do e-mail que abriu a thread (`TP-028`, D9): `Receipt` é pedido
 * novo, os outros dois são o que a triagem já sabe que NÃO entra sozinho na
 * captação (portão do `TP-028` T5). `Undetermined` é estado legítimo — a
 * regra não decidiu, e por fail-open (D3) o ticket entra na captação mesmo
 * assim, com a flag de incerteza.
 */
export type TipoDeMensagem = MessageKind;

export const ROTULOS_DE_TIPO_DE_MENSAGEM: Record<TipoDeMensagem, string> = {
  Receipt: 'Recebimento',
  InProgress: 'Andamento',
  OutOfProcess: 'Fora do processo',
  Undetermined: 'Classificação incerta',
};

export function ehTipoDeMensagem(valor: unknown): valor is TipoDeMensagem {
  return typeof valor === 'string' && Object.hasOwn(ROTULOS_DE_TIPO_DE_MENSAGEM, valor);
}

/**
 * `null` (e-mail sem classificação ainda) e `'Undetermined'` recebem o MESMO
 * tratamento na tela — os dois entram na captação por fail-open (D3) e os
 * dois merecem a flag "classificação incerta". `Receipt` nunca é incerto;
 * `InProgress`/`OutOfProcess` não chegam a esta tela (o portão barra antes).
 */
export function classificacaoIncerta(tipoDeMensagem: TipoDeMensagem | null): boolean {
  return tipoDeMensagem === null || tipoDeMensagem === 'Undetermined';
}

/**
 * A classe do tipo de solicitação que a plataforma entende do que o parser leu (`TP-038`). O que o parser lê são
 * nomes de serviço, não o eixo novo × prorrogação × aditivo (a `D0` do plano assume isso até o cliente dizer o
 * contrário). `OutOfVocabulary` é o texto que não é nenhum dos outros.
 */
export type ClasseDoTipo = RequestTypeClass;

/**
 * Os textos que a operação lê (proposta do `TP-038`, aguardando ok): sem termo técnico. Ventilação mecânica é TIPO
 * de `ID`, não modalidade (glossário): o rótulo diz isso em vez de "VM" (`TP-039`, `D9`).
 */
export const ROTULOS_DE_CLASSE_DO_TIPO: Record<ClasseDoTipo, string> = {
  HomeAssistance: 'Assistência domiciliar (AD)',
  HomeInpatient: 'Internação domiciliar (ID)',
  MechanicalVentilation: 'Ventilação mecânica (ID com ventilação)',
  Pad: 'PAD',
  Nead: 'NEAD',
  OutOfVocabulary: 'Tipo não reconhecido',
};

export function ehClasseDoTipo(valor: unknown): valor is ClasseDoTipo {
  return typeof valor === 'string' && Object.hasOwn(ROTULOS_DE_CLASSE_DO_TIPO, valor);
}

/**
 * A modalidade que a classe do tipo indica (`TP-039`, `D8`/`D9`), calculada NO SERVIDOR (`RequestTypeModality`) e
 * nunca derivada aqui: só `AD` e `ID`, as modalidades ativas do glossário.
 */
export type ModalidadeDaClassificacao = Modality;

const MODALIDADES_DA_CLASSIFICACAO: readonly ModalidadeDaClassificacao[] = ['AD', 'ID'];

export function ehModalidadeDaClassificacao(valor: unknown): valor is ModalidadeDaClassificacao {
  return typeof valor === 'string' && (MODALIDADES_DA_CLASSIFICACAO as readonly string[]).includes(valor);
}

/**
 * O código da modalidade como a tela o escreve, ao lado do tipo: `AD`, `ID` ou `ID com ventilação`, com "(a conferir)"
 * quando a classificação está a conferir, porque a modalidade é tão incerta quanto o tipo de onde vem. `null` = a
 * classe não indica modalidade (PAD, NEAD, fora da lista, sem tipo).
 */
export function textoDaModalidade(classificacao: ClassificacaoDaTriagem): string | null {
  if (classificacao.modalidade === null) return null;
  const codigo = classificacao.ventilacaoContinua ? `${classificacao.modalidade} com ventilação` : classificacao.modalidade;
  return classificacao.aConferir ? `${codigo} (a conferir)` : codigo;
}

/** As classes que a pessoa pode escolher na correção: todas, menos `OutOfVocabulary` (é um resultado, não uma escolha). */
export const CLASSES_ESCOLHIVEIS: readonly ClasseDoTipo[] = ['HomeAssistance', 'HomeInpatient', 'MechanicalVentilation', 'Pad', 'Nead'];

/** De onde veio a classificação (proposta do `TP-038`): regra, agente ou uma pessoa. */
export type OrigemDaClassificacao = ClassificationSource;

export const ROTULOS_DE_ORIGEM_DA_CLASSIFICACAO: Record<OrigemDaClassificacao, string> = {
  Rule: 'Lido pelo sistema',
  Agent: 'Lido pelo agente',
  Human: 'Corrigido por pessoa',
};

export function ehOrigemDaClassificacao(valor: unknown): valor is OrigemDaClassificacao {
  return typeof valor === 'string' && Object.hasOwn(ROTULOS_DE_ORIGEM_DA_CLASSIFICACAO, valor);
}

/** Por que a classificação pede conferência. Sem limiar numérico (`D4`): o que a plataforma observa de verdade. */
export type MotivoDeConferencia = ClassificationReviewReason;

export const ROTULOS_DE_MOTIVO_DE_CONFERENCIA: Record<MotivoDeConferencia, string> = {
  RequestTypeOutOfVocabulary: 'Tipo fora da lista',
  PayerWithoutAlias: 'Operadora não cadastrada',
  RuleAgentDisagreement: 'Leitura em dúvida',
  PayerDivergesFromSender: 'Operadora diferente de quem enviou',
};

export function ehMotivoDeConferencia(valor: unknown): valor is MotivoDeConferencia {
  return typeof valor === 'string' && Object.hasOwn(ROTULOS_DE_MOTIVO_DE_CONFERENCIA, valor);
}

/** A operadora do cadastro que a classificação resolveu. Configuração, nunca dado de paciente. */
export interface OperadoraClassificada {
  readonly id: string;
  readonly codigo: string;
  readonly nome: string;
}

/**
 * A classificação do e-mail de origem (`TP-038`, `D2`: o ticket a assume do e-mail que o abriu). `null` no ticket =
 * ainda não lido, que a tela diz como "Ainda não lido" e nunca como tipo vazio.
 */
export interface ClassificacaoDaTriagem {
  /** `null`: a classificação existe, o tipo é que não foi encontrado no e-mail. */
  readonly classeDoTipo: ClasseDoTipo | null;
  /** `TP-039`: a modalidade que a classe indica. `null` = a classe não indica modalidade. */
  readonly modalidade: ModalidadeDaClassificacao | null;
  /** `TP-039`: `true` só com `ID` vinda de ventilação mecânica. */
  readonly ventilacaoContinua: boolean;
  /** `null`: operadora não encontrada, ou encontrada e não reconhecida (ver `motivos`). */
  readonly operadora: OperadoraClassificada | null;
  readonly origem: OrigemDaClassificacao;
  readonly aConferir: boolean;
  readonly motivos: readonly MotivoDeConferencia[];
}

/** O prazo de uma linha da fila — carrega os dois valores quando discordam (`E5`). */
export interface PrazoDaFila {
  readonly valor: Instante | null;
  readonly origem: OrigemDoPrazo;
  readonly valorDoTicket: Instante | null;
  readonly situacao: SituacaoDoPrazo;
}

/** Uma linha da fila — os campos de `AC-2`, mais `latestSubject` (`S1`/`S5`). */
export interface TicketDaTriagem {
  readonly id: string;
  readonly codigoCurto: string | null;
  readonly nucleo: string | null;
  /** O nome do núcleo, para a tela não mostrar o UUID. `null` quando o ticket não tem núcleo. */
  readonly nomeDoNucleo: string | null;
  readonly chegadaEm: Instante | null;
  readonly contagemDeMensagens: number;
  readonly temEmailDeOrigem: boolean;
  readonly assunto: string | null;
  readonly paciente: CampoDaTriagem;
  readonly operadora: CampoDaTriagem;
  readonly hospital: CampoDaTriagem;
  readonly tipoDeSolicitacao: CampoDaTriagem;
  readonly prazo: PrazoDaFila;
  readonly precisaConfirmacao: boolean;
  /** `TP-028`: classe do e-mail que abriu a thread. `null` = ainda não classificado. */
  readonly tipoDeMensagem: TipoDeMensagem | null;
  /** `TP-035`: número de 15 dígitos do assunto do aviso do SIAI. `null` = assunto fora do formato. */
  readonly numeroSiai: string | null;
  /** `TP-035`: chegada da mensagem mais recente. Com `contagemDeMensagens > 1`, o ticket está atualizado. */
  readonly ultimaMensagemEm: Instante | null;
  /** `TP-038`: a classificação do e-mail de origem. `null` = ainda não lido. */
  readonly classificacao: ClassificacaoDaTriagem | null;
}

/** `GET /triagem` — com `total` de verdade, ao contrário de `/casos`. */
export interface FilaDeTriagem {
  readonly itens: readonly TicketDaTriagem[];
  readonly pagina: number;
  readonly tamanhoDaPagina: number;
  readonly total: number;
}

/** Um anexo do e-mail de origem (`AC-18`, `T16`) — nome e tamanho, nada mais. */
export interface AnexoDaTriagem {
  readonly id: string;
  readonly nomeArquivo: string;
  readonly tamanhoBytes: number | null;
}

/**
 * Uma mensagem da thread do ticket (`US-035`, `AC9`). Sem corpo e sem remetente: o app guarda
 * nenhum dos dois; o `.eml` integro abre por `urlDoEmailDaThread`.
 */
export interface MensagemDaTriagem {
  readonly id: string;
  readonly recebidaEm: Instante;
  readonly assunto: string | null;
  /** A primeira da thread — a que alimenta os campos lidos. */
  readonly ehOrigem: boolean;
  readonly anexos: readonly AnexoDaTriagem[];
  /** O resultado do retorno da autorização marcado nesta mensagem (`TP-056`); `null` = nenhum. */
  readonly retorno: RetornoDaMensagem | null;
}

/** O resultado do retorno de uma mensagem (`TP-056`): o que a operadora decidiu e quem disse. */
export interface RetornoDaMensagem {
  readonly resultado: AuthorizationResult;
  readonly origem: AuthorizationResultSource;
  /** Só do agente, entre 0 e 1; `null` para a escolha de uma pessoa. */
  readonly confianca: number | null;
  /** `null` = ainda a confirmar. Decidido, o resultado não muda mais. */
  readonly decisao: 'Confirmed' | 'Discarded' | null;
}

/**
 * `GET /triagem/{ticketId}` — os dezenove campos de `AC-28` (`TP-026`
 * acrescenta `dataNascimento`/`responsavel`; `TP-027` acrescenta
 * `telefone`/`endereco`/`dispositivo`/`solicitante`/`liminar`/`hd`; `TP-029`
 * acrescenta `frequencia`). A grade da tela mostra dezessete: `cid`/`hd`
 * não entram na lista de campos-com-valor.
 */
export interface TriagemDetalhada {
  readonly id: string;
  readonly codigoCurto: string | null;
  readonly nucleo: string | null;
  readonly temEmailDeOrigem: boolean;
  readonly paciente: CampoDaTriagem;
  readonly cpf: CampoDaTriagem;
  readonly matricula: CampoDaTriagem;
  readonly cid: CampoClinico;
  readonly operadora: CampoDaTriagem;
  readonly hospital: CampoDaTriagem;
  readonly tipoDeSolicitacao: CampoDaTriagem;
  readonly parametrosSolicitados: readonly CampoDaTriagem[];
  readonly prazo: CampoDaTriagem;
  readonly protocolo: CampoDaTriagem;
  /** `TP-026`: data de nascimento — rótulo + validador de formato, mesmo padrão de `paciente`/`cpf`. */
  readonly dataNascimento: CampoDaTriagem;
  /** `TP-026`: responsável PELO ATENDIMENTO, não responsável legal (ver tech-plan). */
  readonly responsavel: CampoDaTriagem;
  /** `TP-027`: telefone de contato — campo livre, mesmo padrão de `paciente`/`cpf`. */
  readonly telefone: CampoDaTriagem;
  /** `TP-027`: endereço — campo livre. */
  readonly endereco: CampoDaTriagem;
  /** `TP-027`: dispositivo/equipamento pedido — campo livre. */
  readonly dispositivo: CampoDaTriagem;
  /** `TP-027`: quem solicitou o atendimento — campo livre. */
  readonly solicitante: CampoDaTriagem;
  /**
   * `TP-027`: indicador de liminar judicial — booleano-like na semântica do
   * extrator (`'sim'` quando presente), carregado como `CampoDaTriagem`
   * comum: `encontrado` já expressa a presença.
   */
  readonly liminar: CampoDaTriagem;
  /**
   * `TP-027`: hipótese diagnóstica (`hd`) — CLÍNICO, mesma disciplina de
   * `cid` (`AC-31`): SEM propriedade de valor.
   */
  readonly hd: CampoClinico;
  /** `TP-029`: frequência da terapia solicitada — campo livre, mesmo padrão de `dispositivo`/`telefone`. */
  readonly frequencia: CampoDaTriagem;
  readonly anexos: readonly AnexoDaTriagem[];
  /** `TP-035`: número da SIAI do assunto, atributo do ticket (a lista devolve o mesmo). */
  readonly numeroSiai: string | null;
  readonly ultimaMensagemEm: Instante | null;
  readonly contagemDeMensagens: number;
  /** `US-035` `AC9`: as mensagens da thread em ordem de chegada, a de origem primeiro. Vazia sem e-mail. */
  readonly mensagens: readonly MensagemDaTriagem[];
  /** `TP-038`: a classificação do e-mail de origem. `null` = ainda não lido. */
  readonly classificacao: ClassificacaoDaTriagem | null;
  /** `TP-038`: as operadoras do cadastro entre as quais a pessoa escolhe na correção. Configuração, sem paciente. */
  readonly operadorasParaEscolha: readonly OperadoraClassificada[];
}

/** `TriagemClinicalFieldSnapshot`, só para quem lê o mapeador. */
export type { TriagemClinicalFieldSnapshot };
